import { useEffect, useRef, useState } from "react";
import api, { formatApiError, fmtDate, openPodFile } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { Upload, ExternalLink, Printer, FileCheck2, Loader2 } from "lucide-react";

export default function PodUpload() {
  const [items, setItems] = useState([]);
  const [cnQuery, setCnQuery] = useState("");
  const [selected, setSelected] = useState(null);
  const [uploading, setUploading] = useState(false);
  const fileRef = useRef(null);

  const load = () => api.get("/consignments").then(({ data }) => setItems(data)).catch(() => {});
  useEffect(() => { load(); }, []);

  useEffect(() => {
    const match = items.find((c) => c.cn_no === cnQuery.trim());
    setSelected(match || null);
  }, [cnQuery, items]);

  const doUpload = async (e) => {
    const f = e.target.files?.[0];
    if (!f || !selected) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", f);
      await api.post(`/consignments/${selected.id}/pod`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success(`POD linked to CN ${selected.cn_no}`);
      await load();
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
    finally { setUploading(false); if (fileRef.current) fileRef.current.value = ""; }
  };

  const withPod = items.filter((c) => c.pod_filename);

  return (
    <div className="p-8 max-w-4xl" data-testid="pod-upload-page">
      <div className="mb-6">
        <h1 className="font-display text-3xl text-slate-900">Proof of Delivery (POD)</h1>
        <p className="text-slate-500 text-sm">Ek CN number chunein aur uska POD (image ya PDF) upload karke link karein</p>
      </div>

      <div className="bg-white border border-slate-100 rounded-xl p-6 mb-6" data-testid="pod-upload-card">
        <div className="flex items-center gap-2 mb-4">
          <FileCheck2 size={18} className="text-[#0b5cc4]" />
          <h2 className="font-semibold text-slate-800">Upload / Link POD</h2>
        </div>
        <div className="space-y-4">
          <div>
            <Label>Select CN Number</Label>
            <Input list="cn-list" value={cnQuery} onChange={(e)=>setCnQuery(e.target.value)} placeholder="Type or pick a CN number" data-testid="pod-cn-select" />
            <datalist id="cn-list">
              {items.map((c) => <option key={c.id} value={c.cn_no} label={`${c.consignor_name} → ${c.consignee_name}`} />)}
            </datalist>
          </div>

          {selected && (
            <div className="rounded-lg border border-slate-200 bg-slate-50 p-4 text-sm" data-testid="pod-selected-info">
              <div className="font-medium text-slate-800">CN {selected.cn_no} · {fmtDate(selected.date)}</div>
              <div className="text-slate-500">{selected.consignor_name} → {selected.consignee_name} · {selected.truck_no}</div>
              {selected.pod_filename ? (
                <div className="mt-3 flex flex-wrap items-center gap-2">
                  <span className="text-green-700 inline-flex items-center gap-1"><FileCheck2 size={14}/> POD linked ({selected.pod_filename})</span>
                  <button onClick={()=>openPodFile(selected.id, false)} className="text-xs px-2 py-1 rounded border border-slate-200 bg-white hover:bg-slate-100 inline-flex items-center gap-1" data-testid="pod-selected-open"><ExternalLink size={13}/> Open</button>
                  <button onClick={()=>openPodFile(selected.id, true)} className="text-xs px-2 py-1 rounded border border-slate-200 bg-white hover:bg-slate-100 inline-flex items-center gap-1" data-testid="pod-selected-print"><Printer size={13}/> Print</button>
                </div>
              ) : (
                <div className="mt-2 text-amber-700 text-xs">Abhi tak koi POD link nahi hai.</div>
              )}
            </div>
          )}

          <input ref={fileRef} type="file" accept="image/*,application/pdf" onChange={doUpload} className="hidden" data-testid="pod-file-upload-input" />
          <Button
            type="button"
            onClick={() => fileRef.current?.click()}
            disabled={!selected || uploading}
            className="btn-primary"
            data-testid="pod-upload-trigger-btn"
          >
            {uploading ? <><Loader2 size={16} className="animate-spin" /> Uploading…</> : <><Upload size={16} /> {selected?.pod_filename ? "Replace POD File" : "Upload POD File"}</>}
          </Button>
          {!selected && <p className="text-xs text-slate-400">Pehle ek valid CN number select karein.</p>}
        </div>
      </div>

      <div className="bg-white border border-slate-100 rounded-xl" data-testid="pod-linked-list">
        <div className="px-4 py-3 border-b border-slate-100 font-semibold text-slate-800 text-sm">Linked PODs ({withPod.length})</div>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-slate-50 text-slate-600">
              <tr>
                <th className="text-left px-4 py-2">C/N No</th>
                <th className="text-left px-4 py-2">Date</th>
                <th className="text-left px-4 py-2">Route</th>
                <th className="text-left px-4 py-2">File</th>
                <th className="text-right px-4 py-2">Actions</th>
              </tr>
            </thead>
            <tbody>
              {withPod.length === 0 && <tr><td colSpan={5} className="px-4 py-8 text-center text-slate-400">Abhi koi POD link nahi hua.</td></tr>}
              {withPod.map((c) => (
                <tr key={c.id} className="border-t border-slate-100">
                  <td className="px-4 py-2 font-mono text-[#0b5cc4]">{c.cn_no}</td>
                  <td className="px-4 py-2">{fmtDate(c.date)}</td>
                  <td className="px-4 py-2">{c.from_location} → {c.to_location}</td>
                  <td className="px-4 py-2 text-slate-500 truncate max-w-[180px]">{c.pod_filename}</td>
                  <td className="px-4 py-2">
                    <div className="flex justify-end gap-1">
                      <button onClick={()=>openPodFile(c.id, false)} className="text-xs px-2 py-1 rounded border border-slate-200 hover:bg-slate-50 inline-flex items-center gap-1"><ExternalLink size={13}/> Open</button>
                      <button onClick={()=>openPodFile(c.id, true)} className="text-xs px-2 py-1 rounded border border-slate-200 hover:bg-slate-50 inline-flex items-center gap-1"><Printer size={13}/> Print</button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
